import { EditablePage } from "@magnolia/react-editor";
import { EditorContextHelper } from "@magnolia/template-annotations";
import { Box } from "@mui/material";
import React, { Suspense } from "react";
import LoadingIndicator from "components/common/Basics/LoadingIndicator";
import { Magnolia } from "domain/Magnolia";
import Services from "services/Services";
import config from "../magnolia.config";
import Error from "../pages/Error";

interface PageLoaderProps {
  routingExceptions: {
    [key: string]: (pagePath: string) => string;
  };
}
interface PageLoaderState {
  pathname: string;
  // customRoutingProvider?: string;
  loading: boolean;
  // eslint-disable-next-line
  content: any;
  error?: string;
  templateDefinitions: { [key: string]: Magnolia.Template } | null;
}
class PageLoader extends React.Component<PageLoaderProps, PageLoaderState> {
  constructor(props: PageLoaderProps) {
    super(props);
    this.state = {
      pathname: "",
      // customRoutingProvider: undefined,
      content: {},
      loading: true,
      templateDefinitions: {} as { [key: string]: Magnolia.Template },
      error: undefined,
    };
  }

  getPagePath = () => {
    const basename = process.env.REACT_APP_MGNL_BASE_AUTHOR || "";
    let route = window.location.pathname.replace(basename, "");

    const exceptionPath = Object.keys(
      this.props.routingExceptions
    ).find((subPath) => window.location.pathname.includes(subPath));

    if (exceptionPath) {
      route = this.props.routingExceptions[exceptionPath](route);
    }

    if (!route.endsWith("/")) {
      route += "/";
    }

    return route;
  };

  loadPage = async () => {
    const pathname = this.getPagePath();

    // Bail out if already loaded content.
    if (this.state.pathname === pathname) return;

    this.setState({
      loading: true,
      pathname,
    });

    try {
      const [pageData] = await Services.content.gatherAllPageData(
        pathname,
        EditorContextHelper.inEditor()
      );

      let templateDefinitions: { [key: string]: Magnolia.Template } | null = null;
      if (EditorContextHelper.inEditor()) {
        // Load page template definition
        templateDefinitions = await Services.content.loadTemplateDefinitions(
          pageData["mgnl:template"]
        );

        // Also load component template definitions for components with nested areas.
        // Without this, EditableArea can't resolve title/availableComponents for areas
        // defined in component templates (e.g., gridLayout's "items" area).
        const componentTemplateIds = new Set<string>();
        const collectTemplateIds = (node: Magnolia.PageMetaData) => {
          if (!node || typeof node !== "object") return;
          if (node["mgnl:template"] && node["mgnl:template"] !== pageData["mgnl:template"]) {
            componentTemplateIds.add(node["mgnl:template"]);
          }
          if (Array.isArray(node["@nodes"])) {
            node["@nodes"].forEach((name) => collectTemplateIds((node as unknown as Record<string, Magnolia.PageMetaData>)[name]));
          }
        };
        collectTemplateIds(pageData);

        const componentDefPromises = Array.from(componentTemplateIds).map((id) =>
          Services.content.loadTemplateDefinitions(id).catch(() => null)
        );
        const componentDefs = await Promise.all(componentDefPromises);
        componentDefs.forEach((defs) => {
          if (defs) Object.assign(templateDefinitions!, defs);
        });
      }

      this.setState({
        loading: false,
        content: pageData,
        templateDefinitions,
      });
    } catch (e) {
      this.setState({
        loading: false,
        error: e instanceof globalThis.Error ? e.message : "error",
      });
    }
  };

  inEditorPreview() {
    const url = window.location.href;
    const inPreview = url.indexOf("mgnlPreview=true") > 0;
    return EditorContextHelper.inEditor() && inPreview;
  }

  componentDidMount() {
    this.loadPage();
    if (EditorContextHelper.inEditor()) {
      if (!this.inEditorPreview()) {
        EditorContextHelper.refresh();
      }
    }
  }

  componentDidUpdate() {
    if (this.getPagePath() !== this.state.pathname) {
      this.loadPage();
      if (EditorContextHelper.inEditor()) {
        if (!this.inEditorPreview()) {
          EditorContextHelper.refresh();
        }
      }
    }
  }

  renderLoadingIndicator = () => {
    return (
      <Box
        height={"100vh"}
        display={"flex"}
        alignItems={"center"}
        justifyContent={"center"}
      >
        <LoadingIndicator />
      </Box>
    );
  };

  render() {
    if (this.state.error) {
      return <Error error={JSON.stringify(this.state.error)} />;
    } else if (this.state.content?.error) {
      return <Error error={JSON.stringify(this.state.content.error)} />;
    } else {
      return (
        <Suspense fallback={this.renderLoadingIndicator()}>
          {this.state.loading ? (
            this.renderLoadingIndicator()
          ) : (
            <EditablePage
              templateDefinitions={this.state.templateDefinitions || {}}
              content={this.state.content}
              config={config}
            />
          )}
        </Suspense>
      );
    }
  }
}

export default PageLoader;
